import dayjs from 'dayjs';
import 'dayjs/locale/fr-ca';
import 'dayjs/locale/en-ca';
import 'dayjs/locale/fr';
import 'dayjs/locale/en';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';
import advancedFormat from 'dayjs/plugin/advancedFormat';

// Extend dayjs with necessary plugins
dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(LocalizedFormat);
dayjs.extend(advancedFormat); // adds the `Do` ordinal token (e.g. "9th")

export type DateFormatType = 'short' | 'long' | 'relative' | 'custom' | 'monthDay';

/**
 * Per-language dayjs template overrides for the abbreviated "month + day"
 * format. A listed language renders via dayjs — e.g. English uses the ordinal
 * `Do` token to produce "Jun 9th". Any language NOT listed falls back to
 * Intl.DateTimeFormat, which lays out month + day correctly for the locale
 * ("9 juin", "9 jun") with no per-locale config. Add an entry only when a
 * language needs non-standard styling the standard Intl output doesn't provide.
 */
const MONTH_DAY_TEMPLATE_OVERRIDES: Record<string, string> = {
  en: 'MMM Do',
};

export interface I18nDateFormatterOptions {
  locale: string;
  timezone?: string;
}

/**
 * Formats a date according to the specified locale and format type.
 * 
 * @param date - The date to format
 * @param format - The format type: 'short', 'long', 'relative', or 'custom'
 * @param locale - The locale to use (e.g., 'en', 'en-CA', 'fr', 'fr-CA')
 * @param customFormat - Optional custom format string (used when format is 'custom')
 * @returns Formatted date string
 * 
 * @example
 * formatI18nDate(new Date(), 'long', 'fr-CA') // "15 octobre 2025 à 14:30"
 * formatI18nDate(new Date(), 'short', 'en-CA') // "October 15, 2025"
 * formatI18nDate(new Date(), 'relative', 'fr-CA') // "il y a 2 heures"
 * formatI18nDate(new Date(), 'custom', 'en', 'MMM DD, YYYY') // "Oct 15, 2025"
 * formatI18nDate('2026-06-09', 'monthDay', 'en') // "Jun 9th"
 * formatI18nDate('2026-06-09', 'monthDay', 'fr-CA') // "9 juin"
 */
export function formatI18nDate(
  date: Date | string | number,
  format: DateFormatType = 'long',
  locale: string = 'en',
  customFormat?: string
): string {
  // Normalize locale to dayjs format (convert 'en-CA' to 'en-ca')
  const dayjsLocale = locale.toLowerCase();
  dayjs.locale(dayjsLocale);
  
  const dateObj = dayjs(date);

  switch (format) {
    case 'relative':
      return dateObj.fromNow();
      
    case 'short': {
      // Use Intl.DateTimeFormat for consistent short format across locales
      const shortOptions: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      };
      return new Intl.DateTimeFormat(locale, shortOptions).format(dateObj.toDate());
    }
      
    case 'long': {
      // Use Intl.DateTimeFormat for consistent long format across locales
      const longOptions: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: !locale.startsWith('fr'), // French uses 24-hour format
      };
      return new Intl.DateTimeFormat(locale, longOptions).format(dateObj.toDate());
    }
      
    case 'monthDay': {
      // A language with an override styles the date via dayjs (e.g. en → the
      // ordinal `Do` token, "Jun 9th"). Otherwise Intl lays out month + day
      // correctly for the locale ("9 juin", "9 jun") with no per-locale config.
      const language = dayjsLocale.split('-')[0];
      const template = MONTH_DAY_TEMPLATE_OVERRIDES[language];
      if (template) {
        // Format via the base-language locale, not the regional variant:
        // dayjs's `ordinal` is only implemented on base `en`, while `en-ca` is
        // a no-op (would drop the suffix → "Jun 9"). Region doesn't affect the
        // abbreviated month, so this is safe and keeps ordinals consistent.
        return dateObj.locale(language).format(template);
      }
      return new Intl.DateTimeFormat(locale, {
        month: 'short',
        day: 'numeric',
      }).format(dateObj.toDate());
    }

    case 'custom':
      if (!customFormat) {
        throw new Error('Custom format string is required when format type is "custom"');
      }
      return dateObj.format(customFormat);

    default:
      return dateObj.format();
  }
}

/**
 * Formats a date to a localized string using dayjs format tokens.
 * Useful when you need full control over the format.
 * 
 * @param date - The date to format
 * @param template - The dayjs format template (e.g., 'MMM DD, YYYY', 'ddd. MMM D')
 * @param locale - The locale to use
 * @returns Formatted date string
 * 
 * @example
 * formatI18nDateWithTemplate(new Date(), 'MMM DD, YYYY', 'fr-CA') // "oct. 15, 2025"
 * formatI18nDateWithTemplate(new Date(), 'ddd. MMM D', 'en-CA') // "Wed. Oct 15"
 * 
 * @see https://day.js.org/docs/en/display/format
 */
export function formatI18nDateWithTemplate(
  date: Date | string | number,
  template: string,
  locale: string = 'en'
): string {
  const dayjsLocale = locale.toLowerCase();
  dayjs.locale(dayjsLocale);
  return dayjs(date).format(template);
}

/**
 * Gets the user's current timezone.
 * 
 * @returns IANA timezone string (e.g., 'America/Toronto')
 */
export function getUserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Formats time only (no date) according to locale conventions.
 * 
 * @param date - The date/time to format
 * @param locale - The locale to use
 * @param includeSeconds - Whether to include seconds in the output
 * @returns Formatted time string
 * 
 * @example
 * formatI18nTime(new Date(), 'en-CA') // "2:30 PM"
 * formatI18nTime(new Date(), 'fr-CA') // "14:30"
 * formatI18nTime(new Date(), 'en-CA', true) // "2:30:45 PM"
 */
export function formatI18nTime(
  date: Date | string | number,
  locale: string = 'en',
  includeSeconds: boolean = false
): string {
  const options: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: '2-digit',
    ...(includeSeconds && { second: '2-digit' }),
    hour12: !locale.startsWith('fr'),
  };
  
  return new Intl.DateTimeFormat(locale, options).format(new Date(date));
}

/**
 * Formats a UTC instant as a "YYYY-MM-DD" calendar date in the given IANA
 * timezone. Useful when a timestamp's calendar day depends on the location
 * (e.g. property-local event date) and must not drift with the viewer's
 * runtime timezone.
 *
 * @param instant - The UTC instant to project into the zone
 * @param ianaZone - IANA timezone identifier (e.g. 'America/Toronto')
 * @returns ISO-style date string "YYYY-MM-DD"
 *
 * @example
 * // 2026-05-08T20:00:00Z is May 8 in NY but May 9 in Tokyo
 * formatInstantAsZonedDate(new Date('2026-05-08T20:00:00Z'), 'America/New_York') // "2026-05-08"
 * formatInstantAsZonedDate(new Date('2026-05-08T20:00:00Z'), 'Asia/Tokyo')        // "2026-05-09"
 */
export function formatInstantAsZonedDate(instant: Date, ianaZone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: ianaZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(instant);
  const get = (type: string) => parts.find(p => p.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/**
 * Formats a UTC instant as a 12-hour wall-clock time in the given IANA
 * timezone, in the compact "H:MMAM/PM" form (no space, period uppercase).
 * Matches the storage shape historically used for open-house times.
 *
 * @param instant - The UTC instant to project into the zone
 * @param ianaZone - IANA timezone identifier (e.g. 'America/Toronto')
 * @returns Wall-clock time string "H:MMAM" or "H:MMPM"
 *
 * @example
 * formatInstantAsZonedTime12h(new Date('2026-05-08T20:00:00Z'), 'America/New_York')    // "4:00PM"
 * formatInstantAsZonedTime12h(new Date('2026-05-08T20:00:00Z'), 'America/Los_Angeles') // "1:00PM"
 */
export function formatInstantAsZonedTime12h(instant: Date, ianaZone: string): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: ianaZone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).formatToParts(instant);
  const get = (type: string) => parts.find(p => p.type === type)?.value ?? '';
  return `${get('hour')}:${get('minute')}${get('dayPeriod').toUpperCase()}`;
}

